import React from "react"
import {
  clearChunkReloadState,
  hasExhaustedChunkReloads,
  isChunkLoadError,
  reloadForNewBuild,
} from "../utils/chunkLoadError"
import { isExpiredSessionError, redirectForExpiredSession } from "../utils/expiredSession"

/**
 * Outermost error boundary. It deliberately sits above every provider and above
 * the router, so it must not use any context — this is the only thing standing
 * between an error in a top level lazy component (CookiesBanner,
 * CustomerSiteWrapper, SiteWrapper) and React unmounting the whole tree, which
 * is what produces a blank white page.
 */
export class RootErrorBoundary extends React.Component<{ children: React.ReactNode }> {
  state: {
    errored: boolean
    error: Error | null
    recovering: boolean
    expiredSession: boolean
  } = {
    errored: false,
    error: null,
    recovering: false,
    expiredSession: false,
  }

  private settleTimer: any = null

  componentDidMount() {
    // Only treat the incident as over once we have stayed up for a while. Doing
    // this on mount would reset the counter before the lazy chunks had even been
    // requested, which would let a genuinely broken build reload forever.
    this.settleTimer = setTimeout(clearChunkReloadState, 15 * 1000)
  }

  componentWillUnmount() {
    clearTimeout(this.settleTimer)
  }

  componentDidCatch(error: Error | null, errorInfo: Record<string, any>) {
    clearTimeout(this.settleTimer)
    console.error(error, errorInfo)

    if (isChunkLoadError(error) && !hasExhaustedChunkReloads()) {
      if (reloadForNewBuild()) {
        this.setState({ errored: true, error, recovering: true })
        return
      }
    }

    // A dead session is not a crash: clear it and go back to the login screen
    // rather than telling the user something went wrong.
    if (isExpiredSessionError(error)) {
      this.setState({ errored: true, error, expiredSession: true })
      redirectForExpiredSession()
      return
    }

    this.setState({ errored: true, error, recovering: false })
  }

  render() {
    if (!this.state.errored) return this.props.children

    if (this.state.recovering) {
      return <RootMessage title="Updating to the latest version…" />
    }

    if (this.state.expiredSession) {
      return (
        <RootMessage
          title="Your session has expired"
          detail="Taking you back to the sign in page. If nothing happens, please refresh."
        />
      )
    }

    const wasChunkError = isChunkLoadError(this.state.error)

    return (
      <RootMessage
        title={wasChunkError ? "A new version is available" : "Something went wrong"}
        detail={
          wasChunkError
            ? "This page is running an old version of the app. Reload to continue."
            : this.state.error?.message
        }
        action={
          <button
            onClick={() => {
              clearChunkReloadState()
              window.location.reload()
            }}
            style={buttonStyle}
          >
            Reload
          </button>
        }
      />
    )
  }
}

/**
 * Fallback for the outermost Suspense boundary. The previous `<div />` rendered
 * a blank page, which was indistinguishable from a crash whenever the initial
 * chunks were slow or failing.
 */
export function RootLoadingFallback() {
  return <RootMessage title="Loading…" spinner />
}

const buttonStyle: React.CSSProperties = {
  marginTop: 24,
  padding: "10px 24px",
  fontSize: 15,
  fontFamily: "inherit",
  color: "#0b1215",
  background: "#7ce0c3",
  border: "none",
  borderRadius: 6,
  cursor: "pointer",
}

function RootMessage({
  title,
  detail,
  action,
  spinner,
}: {
  title: string
  detail?: string | null
  action?: React.ReactNode
  spinner?: boolean
}) {
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 12,
        padding: 24,
        textAlign: "center",
        background: "#ffffff",
        color: "#0b1215",
        fontFamily: "'Space Grotesk', 'Outfit', system-ui, sans-serif",
      }}
    >
      {spinner && (
        <React.Fragment>
          <style>{"@keyframes fos-spin{to{transform:rotate(360deg)}}"}</style>
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: "50%",
              border: "3px solid rgba(11,18,21,0.15)",
              borderTopColor: "#0b1215",
              animation: "fos-spin 0.8s linear infinite",
            }}
          />
        </React.Fragment>
      )}
      <div style={{ fontSize: 17, fontWeight: 500 }}>{title}</div>
      {detail && <div style={{ fontSize: 14, opacity: 0.7, maxWidth: 420 }}>{detail}</div>}
      {action}
    </div>
  )
}
